import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ErrorComponent from '../components/ErrorComponent.jsx';

const api = axios.create({
	baseURL: process.env.REACT_APP_API_BASE || "",
	headers: {
		"Content-Type": "application/json"
	}
});

export const ApiContext = createContext(null);

// Interceptor installati una volta sola (indipendente dal lifecycle React)
let interceptorsInstalled = false;

// Refs globali per far funzionare l’interceptor anche se il Provider monta/smonta
const errorSetterRef = { current: null };
const getRefreshTokenRef = { current: null };
const onTokenRefreshedRef = { current: null };

const installInterceptorsOnce = () => {
	if (interceptorsInstalled) return;
	interceptorsInstalled = true;

	const MAX_RETRIES = (location.hostname == "localhost" ? 10 : 30);
	const BASE_DELAY_MS = 300;
	const MAX_DELAY_MS = 8000;

	const isCanceled = (err) =>
		err?.code === "ERR_CANCELED" ||
		err?.name === "CanceledError" ||
		err?.name === "AbortError" ||
		axios.isCancel?.(err) === true;

	const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

	const getDelayMs = (attempt) => {
		const exp = BASE_DELAY_MS * (2 ** (attempt - 1));
		const capped = Math.min(exp, MAX_DELAY_MS);
		const jitterFactor = 0.5 + (Math.random() * 0.5);
		return Math.floor(capped * jitterFactor);
	};

	api.interceptors.request.use(
		(config) => {
			const urlSearch = new URLSearchParams(window.location.search);
			if (urlSearch.get("t") && urlSearch.get("t") == "nocache") {
				config.params = { ...config.params, timestamp: Date.now() };
			}
			return config;
		},
		(error) => Promise.reject(error)
	);

	api.interceptors.response.use(
		(res) => res,
		async (error) => {
			const status = error?.response?.status;
			const url = error?.config?.url || "";
			const originalRequest = error?.config;

			// Retry SOLO se non ho response (problemi rete / CORS / ecc.)
			if (!status && originalRequest && !isCanceled(error)) {
				originalRequest.__retryCount = originalRequest.__retryCount ?? 0;

				if (originalRequest.__retryCount < MAX_RETRIES) {
					originalRequest.__retryCount += 1;

					const delayMs = getDelayMs(originalRequest.__retryCount);
					await sleep(delayMs);

					console.log(`Network error: retrying request to ${url} (attempt ${originalRequest.__retryCount})`);

					if (originalRequest.__retryCount >= 3) {
						const toastId = 'network-error-retry';
						if (!toast.isActive(toastId)) {
							toast.error('Ci sono problemi sulla tua rete. Stiamo provando a riconnetterci. Attendi...', {
								toastId,
								position: 'top-right',
								autoClose: 15000,
								hideProgressBar: false,
								closeOnClick: true,
								pauseOnHover: false,
								draggable: false,
								theme: 'colored',
							});
						}
					}

					return api(originalRequest);
				}
			}

			// Gestione 401: refresh SOLO se qualcuno ha registrato getRefreshToken (Game vero)
			if (status === 401 && !/refresh-token/.test(url)) {
				const refreshToken = getRefreshTokenRef.current?.();

				// Demo (o Game senza refresh token): niente refresh
				if (!refreshToken) {
					errorSetterRef.current?.(error);
					return Promise.reject(error);
				}

				try {
					const body = new URLSearchParams();
					body.append("token", refreshToken);

					const res = await api.post(`/api/auth/refresh-token`, body.toString(), {
						headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
					});

					onTokenRefreshedRef.current?.(res.data);

					originalRequest.headers = originalRequest.headers ?? {};
					originalRequest.headers.Authorization = `Bearer ${res.data.access_token}`;
					return api(originalRequest);
				} catch (refreshError) {
					if (refreshError?.response?.status === 401) {
						window.location.reload();
					}
					errorSetterRef.current?.(refreshError);
					return Promise.reject(refreshError);
				}
			}

			errorSetterRef.current?.(error);
			return Promise.reject(error);
		}
	);
};

export const ApiProvider = ({ children }) => {
	const getIsDemo = () => {
		if (typeof window === 'undefined' || !window.location) return false;
		return window.location.pathname.startsWith('/trivia/demo');
	};

	const [isDemo, setIsDemo] = useState(getIsDemo);

	const [error, setError] = useState(null);
	const [errorObj, setErrorObj] = useState(null);

	// installa interceptor (idempotente) prima possibile
	installInterceptorsOnce();

	// collega setError alla ref globale usata nell’interceptor
	useEffect(() => {
		errorSetterRef.current = setError;
		return () => {
			if (errorSetterRef.current === setError) errorSetterRef.current = null;
		};
	}, []);

	const setAuthAccessors = ({ getRefreshToken, onTokenRefreshed }) => {
		getRefreshTokenRef.current = getRefreshToken ?? null;
		onTokenRefreshedRef.current = onTokenRefreshed ?? null;
	};

	useEffect(() => {
		const onLocationChange = () => setIsDemo(getIsDemo());

		window.addEventListener('popstate', onLocationChange);

		const _pushState = history.pushState;
		const _replaceState = history.replaceState;

		history.pushState = function () {
			_pushState.apply(this, arguments);
			window.dispatchEvent(new Event('locationchange'));
		};

		history.replaceState = function () {
			_replaceState.apply(this, arguments);
			window.dispatchEvent(new Event('locationchange'));
		};

		window.addEventListener('locationchange', onLocationChange);

		return () => {
			window.removeEventListener('popstate', onLocationChange);
			window.removeEventListener('locationchange', onLocationChange);
			history.pushState = _pushState;
			history.replaceState = _replaceState;
		};
	}, []);

	useEffect(() => {
		if (!error) {
			setErrorObj(null);
			return;
		}

		const status = error?.response?.status ?? error?.status ?? error?.code;

		let errorKey, errorCode;
		switch (status) {
			case 400:
			case 401:
				errorKey = "Accesso non autorizzato";
				errorCode = "401";
				break;
			case 404:
				errorKey = "Pagina non trovata";
				errorCode = "404";
				break;
			case "ERR_CANCELED":
				errorKey = null;
				errorCode = null;
				break;
			case "ERR_NETWORK":
				errorKey = "Si è verificato un errore sulla tua rete";
				errorCode = `<i class="bi bi-wifi-off"></i>`;
				break;
			default:
				errorKey = "Si è verificato un errore imprevisto";
				errorCode = String(status || 500);
				break;
		}

		if (errorKey && errorCode) {
			setErrorObj({
				title: errorKey,
				errorCode
			});
		}
	}, [error]);

	const value = useMemo(() => ({
		api,
		isDemo,
		errorObj,
		clearError: () => setError(null),
		setAuthAccessors
	}), [isDemo, errorObj]);

	return (
		<ApiContext.Provider value={value}>
			{errorObj ? <ErrorComponent item={errorObj} /> : <>{children}</>}
		</ApiContext.Provider>
	);
};

export const useApiContext = () => {
	const ctx = useContext(ApiContext);
	if (!ctx) throw new Error('useApiContext must be used within <ApiProvider />');
	return ctx;
};